import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { supabase } from '@/lib/supabase';
import { dashboardEvents as mockEvents } from '@/mocks/dashboard';
import { PageHeader } from '@/pages/dashboard/shared';
import { EVENT_TYPE_CONFIG, WEEKDAYS, MONTHS } from '@/pages/dashboard/shared-data';
import NewEventModal from '@/pages/dashboard/calendario/components/NewEventModal';
import type { CalendarEvent } from '@/mocks/dashboard';

const EVENT_LINK: Record<CalendarEvent['type'], string> = {
  manutencao: '/ordens-servico',
  vistoria: '/ordens-servico',
  mudanca: '/ordens-servico',
  alvara: '/admin/alvaras',
  reuniao: '/condominios',
  outros: '/condominios',
};

/** Para onde o compromisso leva. A reunião abre a aba que a mantém. */
function linkDoEvento(ev: CalendarEvent): string {
  if (ev.origem === 'reuniao' && ev.condominio_id) {
    return `/condominios/${ev.condominio_id}/reunioes`;
  }
  return EVENT_LINK[ev.type];
}

const VALID_TYPES = new Set(['manutencao', 'vistoria', 'mudanca', 'alvara', 'reuniao', 'outros']);

// A agenda soma `eventos` e as reuniões Agendada/Realizada (migration 0053).
// Ler as duas tabelas aqui e juntar no cliente daria dois estados para manter
// em sincronia e duas listas para ordenar; a view resolve na consulta, e a RLS
// das tabelas de origem continua valendo (`security_invoker`).
const SELECT_AGENDA =
  'id, origem, origem_id, titulo, data, hora, tipo, condominio_id, condominio_nome, status';

type CondoOption = { id: string; nome: string };

function toCalendarEvent(row: any): CalendarEvent {
  return {
    id: String(row.id),
    title: row.titulo,
    condo: row.condominio_nome || 'Sem condomínio',
    date: row.data,
    // Reunião não tem hora no cadastro: sem hora, o compromisso vai para o fim
    // do dia, que é como o resto da tela já trata `hora` nula.
    time: row.hora ? row.hora.slice(0, 5) : '23:59',
    type: VALID_TYPES.has(row.tipo) ? row.tipo : 'outros',
    condominio_id: row.condominio_id || undefined,
    origem: row.origem === 'reuniao' ? 'reuniao' : 'evento',
  };
}

export default function CalendarioPage() {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<string>(today.toISOString().slice(0, 10));
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);
  const [condoList, setCondoList] = useState<CondoOption[]>([]);
  const [selectedCondoId, setSelectedCondoId] = useState<string>('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState<string>(today.toISOString().slice(0, 10));

  // Refresh events from Supabase
  const refreshEvents = useCallback(() => {
    // O builder do supabase-js e apenas "thenable": Promise.resolve da acesso a .catch.
    Promise.resolve(
      supabase
        .from('vw_agenda')
        .select(SELECT_AGENDA)
        .order('data', { ascending: true })
        .order('hora', { ascending: true, nullsFirst: false }),
    )
      .then(({ data, error }) => {
        if (error || !data) {
          console.warn('Falling back to mock events after refresh:', error?.message);
          return;
        }
        setEvents(data.map(toCalendarEvent));
        setUsingFallback(false);
      })
      .catch(() => {});
  }, []);

  const openNewEventModal = (dateStr: string) => {
    setModalDate(dateStr);
    setModalOpen(true);
  };

  // Fetch condominios + events
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setUsingFallback(false);

    // Fetch condominios for filter dropdown
    Promise.resolve(
      supabase.from('condominios').select('id, nome').order('nome', { ascending: true }),
    )
      .then(({ data }) => {
        if (!cancelled && data) setCondoList(data);
      })
      .catch(() => {});

    // Fetch events
    Promise.resolve(
      supabase
        .from('vw_agenda')
        .select(SELECT_AGENDA)
        .order('data', { ascending: true })
        .order('hora', { ascending: true, nullsFirst: false }),
    )
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          console.warn('Falling back to mock events:', error?.message);
          setEvents(mockEvents);
          setUsingFallback(true);
        } else {
          setEvents(data.map(toCalendarEvent));
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setEvents(mockEvents);
        setUsingFallback(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Filter events by selected condo
  const filteredEvents = useMemo(() => {
    if (!selectedCondoId) return events;
    return events.filter((ev) => ev.condominio_id === selectedCondoId);
  }, [events, selectedCondoId]);

  const eventsByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    filteredEvents.forEach((ev) => {
      if (!map[ev.date]) map[ev.date] = [];
      map[ev.date].push(ev);
    });
    return map;
  }, [filteredEvents]);

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const goToToday = () => {
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    setSelectedDate(today.toISOString().slice(0, 10));
  };

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();
  const days: (number | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const getDateStr = (day: number) => {
    const m = String(currentMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${currentYear}-${m}-${d}`;
  };

  const todayStr = today.toISOString().slice(0, 10);
  const selectedEvents = eventsByDate[selectedDate] || [];
  const totalEventsThisMonth = Object.keys(eventsByDate)
    .filter((d) => {
      const [y, m] = d.split('-').map(Number);
      return y === currentYear && m === currentMonth + 1;
    })
    .reduce((sum, d) => sum + (eventsByDate[d]?.length || 0), 0);

  const formatDisplayDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return `${d} de ${MONTHS[m - 1]} de ${y}`;
  };

  const todayLabel = selectedDate === todayStr ? 'Hoje' : formatDisplayDate(selectedDate);

  const selectedCondoName = selectedCondoId
    ? condoList.find((c) => c.id === selectedCondoId)?.nome || ''
    : '';

  // Upcoming events (next 7 days from today)
  const upcomingEvents = useMemo(() => {
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const endDate = new Date(todayDate);
    endDate.setDate(endDate.getDate() + 7);

    return filteredEvents
      .filter((ev) => {
        const evDate = new Date(ev.date + 'T00:00:00');
        return evDate >= todayDate && evDate <= endDate;
      })
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  }, [filteredEvents]);

  // Last 7 days events
  const recentEvents = useMemo(() => {
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const startDate = new Date(todayDate);
    startDate.setDate(startDate.getDate() - 7);

    return filteredEvents
      .filter((ev) => {
        const evDate = new Date(ev.date + 'T00:00:00');
        return evDate >= startDate && evDate < todayDate;
      })
      .sort((a, b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time));
  }, [filteredEvents]);

  // Stats for this month
  const monthStats = useMemo(() => {
    const byType: Record<string, number> = {};
    Object.keys(eventsByDate).forEach((d) => {
      const [y, m] = d.split('-').map(Number);
      if (y === currentYear && m === currentMonth + 1) {
        (eventsByDate[d] || []).forEach((ev) => {
          byType[ev.type] = (byType[ev.type] || 0) + 1;
        });
      }
    });
    return byType;
  }, [eventsByDate, currentMonth, currentYear]);

  const totalManutencoes = (monthStats.manutencao || 0) + (monthStats.vistoria || 0);
  const totalMudancas = monthStats.mudanca || 0;
  const totalReunioes = monthStats.reuniao || 0;
  const totalAlvaras = monthStats.alvara || 0;

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <PageHeader
          title="Calendário e Compromissos"
          subtitle={
            selectedCondoId
              ? `Eventos de ${selectedCondoName}`
              : 'Agenda consolidada de todos os condomínios'
          }
        />

        {/* Filter bar */}
        <div className="mb-6 flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-colors text-sm font-medium whitespace-nowrap ${
                selectedCondoId
                  ? 'bg-primary-50 border-primary-300 text-primary-800'
                  : 'bg-background-50 border-background-300/60 text-foreground-700 hover:bg-background-100'
              }`}
            >
              <i
                className={`text-base ${selectedCondoId ? 'ri-building-2-fill' : 'ri-building-2-line'}`}
              ></i>
              <span>{selectedCondoId ? selectedCondoName : 'Todos os condomínios'}</span>
              <i
                className={`${filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs transition-transform`}
              ></i>
            </button>

            {filterOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setFilterOpen(false)}></div>
                <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 border border-background-200/70 rounded-lg shadow-lg z-20 overflow-hidden">
                  <div className="max-h-64 overflow-y-auto py-1">
                    <button
                      onClick={() => {
                        setSelectedCondoId('');
                        setFilterOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                        !selectedCondoId
                          ? 'bg-primary-50 text-primary-800 font-medium'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      <i className="ri-global-line text-base"></i>
                      <span>Todos os condomínios</span>
                      {!selectedCondoId && (
                        <i className="ri-check-line ml-auto text-primary-600"></i>
                      )}
                    </button>
                    {condoList.map((condo) => (
                      <button
                        key={condo.id}
                        onClick={() => {
                          setSelectedCondoId(condo.id);
                          setFilterOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                          selectedCondoId === condo.id
                            ? 'bg-primary-50 text-primary-800 font-medium'
                            : 'text-foreground-700 hover:bg-background-100'
                        }`}
                      >
                        <i className="ri-building-2-line text-base"></i>
                        <span>{condo.nome}</span>
                        {selectedCondoId === condo.id && (
                          <i className="ri-check-line ml-auto text-primary-600"></i>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {selectedCondoId && (
            <button
              onClick={() => setSelectedCondoId('')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-close-line"></i>
              <span>Limpar filtro</span>
            </button>
          )}

          {!loading && (
            <span className="text-xs text-foreground-500 ml-auto">
              {filteredEvents.length} evento(s) encontrado(s)
            </span>
          )}
        </div>

        {/* Loading shimmer */}
        {loading && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-background-50 border border-background-200/70 rounded-lg p-4 animate-pulse"
              >
                <div className="w-8 h-8 rounded-lg bg-background-200 mb-2"></div>
                <div className="h-6 w-10 bg-background-200 rounded mb-1"></div>
                <div className="h-3 w-20 bg-background-100 rounded"></div>
              </div>
            ))}
          </div>
        )}

        {/* Fallback notice */}
        {!loading && usingFallback && (
          <div className="mb-6 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-database-2-line text-amber-600 text-xs"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-amber-800">Dados do armazenamento local</p>
              <p className="text-xs text-amber-600 mt-0.5">
                Não foi possível carregar os eventos do banco. Exibindo dados de exemplo.
              </p>
            </div>
          </div>
        )}

        {/* Quick stats row */}
        {!loading && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {[
              {
                label: 'Manutenções/Vistorias',
                value: totalManutencoes,
                icon: 'ri-tools-line',
                color: 'bg-secondary-50 text-secondary-600',
              },
              {
                label: 'Mudanças',
                value: totalMudancas,
                icon: 'ri-truck-line',
                color: 'bg-accent-50 text-accent-600',
              },
              {
                label: 'Reuniões',
                value: totalReunioes,
                icon: 'ri-group-line',
                color: 'bg-emerald-50 text-emerald-600',
              },
              {
                label: 'Alvarás vencendo',
                value: totalAlvaras,
                icon: 'ri-file-warning-line',
                color: 'bg-red-50 text-red-600',
              },
            ].map((item) => (
              <div
                key={item.label}
                className="bg-background-50 border border-background-200/70 rounded-lg p-4"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center`}
                  >
                    <i className={`${item.icon} text-sm`}></i>
                  </div>
                </div>
                <p className="text-xl font-bold text-foreground-900">{item.value}</p>
                <p className="text-xs text-foreground-500 mt-0.5 whitespace-nowrap">{item.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Calendar */}
        {!loading && (
          <div className="bg-background-50 border border-background-200/70 rounded-lg mb-6">
            <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-base font-semibold text-foreground-900">Calendário</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">
                  {totalEventsThisMonth} compromisso(s) em {MONTHS[currentMonth]}
                </span>
                <button
                  onClick={goToToday}
                  className="px-2.5 py-1 rounded-md border border-background-300/60 text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  Hoje
                </button>
              </div>
            </div>
            <div className="p-5 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={prevMonth}
                  className="w-8 h-8 rounded-md border border-background-300/60 flex items-center justify-center text-foreground-600 hover:bg-background-100 transition-colors"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <span className="text-sm font-semibold text-foreground-900">
                  {MONTHS[currentMonth]} {currentYear}
                </span>
                <button
                  onClick={nextMonth}
                  className="w-8 h-8 rounded-md border border-background-300/60 flex items-center justify-center text-foreground-600 hover:bg-background-100 transition-colors"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>

              <div className="grid grid-cols-7 mb-2">
                {WEEKDAYS.map((w) => (
                  <div
                    key={w}
                    className="text-center text-[11px] font-semibold text-foreground-400 uppercase tracking-wider py-1"
                  >
                    {w}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 border-t border-l border-background-200/70 rounded-lg overflow-hidden">
                {days.map((day, idx) => {
                  if (day === null)
                    return (
                      <div
                        key={`empty-${idx}`}
                        className="border-r border-b border-background-200/50 bg-background-50/50 min-h-[72px]"
                      ></div>
                    );
                  const dateStr = getDateStr(day);
                  const dayEvents = eventsByDate[dateStr] || [];
                  const isToday = dateStr === todayStr;
                  const isSelected = dateStr === selectedDate;
                  const uniqueTypes = [...new Set(dayEvents.map((e) => e.type))];
                  return (
                    <button
                      key={dateStr}
                      onClick={() => setSelectedDate(dateStr)}
                      className={`border-r border-b border-background-200/50 min-h-[72px] p-1.5 text-left transition-colors hover:bg-background-100 relative group ${isSelected ? 'bg-secondary-50/80' : 'bg-background-50'}`}
                    >
                      <div className="flex items-start justify-between mb-0.5">
                        <span
                          className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${isToday ? 'bg-primary-500 text-background-50' : isSelected ? 'text-foreground-900' : 'text-foreground-700'}`}
                        >
                          {day}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openNewEventModal(dateStr);
                          }}
                          className="w-5 h-5 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-50 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                          title="Adicionar compromisso"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-0.5">
                        {uniqueTypes.slice(0, 3).map((type) => (
                          <span
                            key={type}
                            className={`inline-block w-[6px] h-[6px] rounded-full ${EVENT_TYPE_CONFIG[type].dotColor}`}
                            title={EVENT_TYPE_CONFIG[type].label}
                          ></span>
                        ))}
                        {uniqueTypes.length > 3 && (
                          <span className="text-[9px] text-foreground-400 leading-none">
                            +{uniqueTypes.length - 3}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-background-200/50">
                {Object.entries(EVENT_TYPE_CONFIG).map(([key, config]) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <span
                      className={`inline-block w-[8px] h-[8px] rounded-full ${config.dotColor}`}
                    ></span>
                    <span className="text-[11px] text-foreground-500">{config.label}</span>
                  </div>
                ))}
              </div>

              {/* Selected day events */}
              <div className="mt-5 pt-4 border-t border-background-200/50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground-900">
                    {todayLabel}
                    {selectedEvents.length === 0 && (
                      <span className="text-foreground-500 font-normal ml-1">
                        — Nenhum compromisso
                      </span>
                    )}
                  </h3>
                  <button
                    onClick={() => openNewEventModal(selectedDate)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-xs font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line text-sm"></i>
                    <span>Novo compromisso</span>
                  </button>
                </div>
                {selectedEvents.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {selectedEvents
                      .sort((a, b) => a.time.localeCompare(b.time))
                      .map((ev) => {
                        const config = EVENT_TYPE_CONFIG[ev.type];
                        const linkTo = linkDoEvento(ev);
                        return (
                          <Link
                            key={ev.id}
                            to={linkTo}
                            className="flex items-start gap-3 p-3 rounded-lg border border-background-200/70 hover:border-background-300 transition-colors cursor-pointer no-underline"
                          >
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${config.badgeColor.split(' ')[0]}`}
                            >
                              <i
                                className={`${config.icon} text-sm ${config.badgeColor.split(' ')[1]}`}
                              ></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground-800">{ev.title}</p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span
                                  className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border ${config.badgeColor}`}
                                >
                                  {config.label}
                                </span>
                                <span className="text-xs text-foreground-500">{ev.condo}</span>
                                {ev.time && ev.time !== '23:59' && (
                                  <>
                                    <span className="text-xs text-foreground-400">•</span>
                                    <span className="text-xs text-foreground-500">{ev.time}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Loading placeholder for lists */}
        {loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 animate-pulse">
              <div className="h-5 w-32 bg-background-200 rounded mb-4"></div>
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-background-100 rounded-lg mb-2"></div>
              ))}
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 animate-pulse">
              <div className="h-5 w-32 bg-background-200 rounded mb-4"></div>
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-background-100 rounded-lg mb-2"></div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming + Recent events */}
        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Próximos 7 dias */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg">
              <div className="px-5 py-4 border-b border-background-200/70 flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-primary-100 flex items-center justify-center">
                  <i className="ri-calendar-todo-line text-primary-600 text-xs"></i>
                </div>
                <h2 className="text-base font-semibold text-foreground-900">Próximos 7 dias</h2>
              </div>
              <div className="p-4">
                {upcomingEvents.length === 0 ? (
                  <p className="text-sm text-foreground-500 text-center py-6">
                    Nenhum compromisso nos próximos 7 dias
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {upcomingEvents.map((ev) => {
                      const config = EVENT_TYPE_CONFIG[ev.type];
                      const linkTo = linkDoEvento(ev);
                      const evDate = new Date(ev.date + 'T00:00:00');
                      const dayName = WEEKDAYS[evDate.getDay()];
                      const day = evDate.getDate();
                      const month = MONTHS[evDate.getMonth()];
                      return (
                        <Link
                          key={ev.id}
                          to={linkTo}
                          className="flex items-start gap-3 p-3 rounded-lg border border-background-200/70 hover:border-background-300 transition-colors cursor-pointer no-underline"
                        >
                          <div className="text-center shrink-0 min-w-[44px]">
                            <p className="text-xs font-medium text-foreground-500">{dayName}</p>
                            <p className="text-lg font-bold text-foreground-900">{day}</p>
                            <p className="text-[10px] text-foreground-400">{month.slice(0, 3)}</p>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground-800">{ev.title}</p>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              <span
                                className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${config.badgeColor}`}
                              >
                                {config.label}
                              </span>
                              <span className="text-xs text-foreground-500">{ev.condo}</span>
                              {ev.time && ev.time !== '23:59' && (
                                <span className="text-xs text-foreground-500">{ev.time}</span>
                              )}
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Últimos 7 dias */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg">
              <div className="px-5 py-4 border-b border-background-200/70 flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-secondary-100 flex items-center justify-center">
                  <i className="ri-history-line text-secondary-600 text-xs"></i>
                </div>
                <h2 className="text-base font-semibold text-foreground-900">Últimos 7 dias</h2>
              </div>
              <div className="p-4">
                {recentEvents.length === 0 ? (
                  <p className="text-sm text-foreground-500 text-center py-6">
                    Nenhum compromisso nos últimos 7 dias
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {recentEvents.map((ev) => {
                      const config = EVENT_TYPE_CONFIG[ev.type];
                      const linkTo = linkDoEvento(ev);
                      const evDate = new Date(ev.date + 'T00:00:00');
                      const dayName = WEEKDAYS[evDate.getDay()];
                      const day = evDate.getDate();
                      const month = MONTHS[evDate.getMonth()];
                      return (
                        <Link
                          key={ev.id}
                          to={linkTo}
                          className="flex items-start gap-3 p-3 rounded-lg border border-background-200/70 hover:border-background-300 transition-colors cursor-pointer no-underline"
                        >
                          <div className="text-center shrink-0 min-w-[44px] opacity-60">
                            <p className="text-xs font-medium text-foreground-500">{dayName}</p>
                            <p className="text-lg font-bold text-foreground-900">{day}</p>
                            <p className="text-[10px] text-foreground-400">{month.slice(0, 3)}</p>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground-800">{ev.title}</p>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              <span
                                className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${config.badgeColor}`}
                              >
                                {config.label}
                              </span>
                              <span className="text-xs text-foreground-500">{ev.condo}</span>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <NewEventModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        prefilledDate={modalDate}
        condoList={condoList}
        onCreated={refreshEvents}
      />
    </DashboardLayout>
  );
}
